/*!
 * Oneskin PLP — vanilla JS controller for the collection and search product
 * listing pages: filter bar / drawer / sidebar, price range slider, infinite
 * scroll, AJAX pagination, sticky filter sidebar and reveal animations.
 *
 * Detached from the frontend/ Vite bundle. Loaded directly via:
 *   <script src="{{ 'plp.js' | asset_url }}" defer="defer"></script>
 *
 * Replaces:
 *   frontend/scripts/templates/collection.js
 *   frontend/scripts/templates/search.js
 *   frontend/scripts/lib/plp-ajax.js
 *   frontend/scripts/lib/pagination.js
 *   frontend/scripts/lib/fetch-data.js
 *   frontend/scripts/lib/sticky-scroll.js
 *   frontend/scripts/lib/filters/* (filter-handler, filtering, filter-bar,
 *     filter-drawer, filter-sidebar, price-range, events)
 *   frontend/scripts/lib/animation/{collection,search,search-banner,filter-drawer,filter-sidebar}.js
 *   frontend/scripts/lib/animation/{shouldAnimate,delay-offset,intersection-watcher}.js
 *   frontend/scripts/lib/{at-breakpoint-change,media-queries,update-inner-html}.js
 *
 * npm dependencies replaced with native equivalents:
 *   ajaxinate        -> initInfiniteScroll()
 *   @fluorescent/dom -> querySelector / classList helpers
 *   ftdomdelegate    -> delegated click listener
 *   evx              -> internal bus() for PLP-private events, DOM CustomEvents
 *                       for anything crossing an asset boundary
 *   focus-trap       -> createFocusTrap()
 *   body-scroll-lock -> lockBodyScroll() / unlockBodyScroll() (matches the
 *                       `overflow-hidden` class pattern in assets/quick-view-modal.js)
 *   slide-anim       -> slideDown() / slideUp() / slideStop()
 *   nouislider       -> createRangeSlider(), which builds the same `noUi-*` DOM
 *                       and exposes a compatible `el.noUiSlider` API so the
 *                       existing vendor CSS still applies
 *
 * All existing data-attribute / class hooks are preserved so no Liquid markup
 * changes were required. `collection:updated` and `product-item:reinit` are
 * dispatched as DOM CustomEvents after every DOM update so assets/product-item.js
 * and the Storista widget script in main-collection-product-grid.liquid keep working.
 */
;(() => {
  'use strict'

  // Collection + search sections each emit this script tag; guard against double-init.
  if (window.__oneskinPlp) return
  window.__oneskinPlp = true

  const strings = (window.theme && window.theme.strings) || {}

  /* -------------------------------------------------------------------------
   * DOM helpers (replaces @fluorescent/dom)
   * ---------------------------------------------------------------------- */

  function qs(selector, ctx) {
    return (ctx || document).querySelector(selector)
  }

  function qsa(selector, ctx) {
    return Array.from((ctx || document).querySelectorAll(selector))
  }

  function toArray(target) {
    if (!target) return []
    return Array.isArray(target) || target instanceof NodeList ? Array.from(target) : [target]
  }

  function addClass(target, className) {
    toArray(target).forEach((el) => el.classList.add(className))
  }

  function removeClass(target, className) {
    toArray(target).forEach((el) => el.classList.remove(className))
  }

  function toggleClass(target, className, force) {
    toArray(target).forEach((el) => el.classList.toggle(className, force))
  }

  function listen(target, event, handler, options) {
    const nodes = toArray(target)
    nodes.forEach((node) => node.addEventListener(event, handler, options))
    return () => nodes.forEach((node) => node.removeEventListener(event, handler, options))
  }

  function debounce() {
    let timer
    return function (func, time = 100) {
      if (timer) clearTimeout(timer)
      timer = setTimeout(func, time)
    }
  }

  function getMediaQuery(size) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(`--media-${size}`)
    return value || false
  }

  function lerp(start, end, amount) {
    return (1 - amount) * start + amount * end
  }

  function atBreakpointChange(breakpointToWatch, callback) {
    let screenUnderBP = (window.innerWidth || document.documentElement.clientWidth) <= breakpointToWatch

    const widthWatcher = () => {
      const currentScreenWidthUnderBP = window.innerWidth <= breakpointToWatch
      if (currentScreenWidthUnderBP !== screenUnderBP) {
        screenUnderBP = currentScreenWidthUnderBP
        return callback()
      }
    }

    window.addEventListener('resize', widthWatcher)

    return {
      unload() {
        window.removeEventListener('resize', widthWatcher)
      },
    }
  }

  /**
   * Takes a selector and updates the innerHTML of that element with the
   * contents found in the updated document returned by a fetch request.
   */
  function updateInnerHTML(selector, doc) {
    const updatedItem = qs(selector, doc)
    const oldItem = qs(selector)
    if (updatedItem && oldItem) {
      oldItem.innerHTML = updatedItem.innerHTML
    }
  }

  /* -------------------------------------------------------------------------
   * Internal event bus (replaces evx for PLP-private events)
   * ---------------------------------------------------------------------- */

  function createBus() {
    const listeners = {}

    return {
      emit(name, payload) {
        ;(listeners[name] || []).slice().forEach((callback) => callback(payload))
      },
      on(name, callback) {
        listeners[name] = listeners[name] || []
        listeners[name].push(callback)
        return () => {
          listeners[name] = (listeners[name] || []).filter((registered) => registered !== callback)
        }
      },
    }
  }

  const bus = createBus()

  const FILTERS_UPDATE = 'collection:filters:update'
  const FILTERS_REMOVE = 'collection:filters:remove'
  const RANGE_REMOVE = 'collection:range:remove'
  const EVERYTHING_CLEAR = 'collection:clear'
  const FILTER_REMOVED = 'filters:filter-removed'

  /* -------------------------------------------------------------------------
   * Cross-asset events. `collection:updated` and `product-item:reinit` must
   * stay DOM CustomEvents: assets/product-item.js and the Storista script in
   * main-collection-product-grid.liquid listen for them.
   * ---------------------------------------------------------------------- */

  function reinitProductItems(root) {
    document.dispatchEvent(new CustomEvent('product-item:reinit', { detail: { root } }))
  }

  function emitCollectionUpdated() {
    document.dispatchEvent(new CustomEvent('collection:updated'))
  }

  /* -------------------------------------------------------------------------
   * Animation helpers
   * ---------------------------------------------------------------------- */

  function shouldAnimate(node) {
    return (
      !!node &&
      node.classList.contains('animation') &&
      !document.documentElement.classList.contains('prefers-reduced-motion')
    )
  }

  /**
   * Sets the `--delay-offset-multiplier` variable on each matched item in the
   * order the selectors are passed.
   */
  function delayOffset(node, selectors, offsetStart = 0) {
    if (!node) return
    let offset = offsetStart
    selectors.forEach((selector) => {
      qsa(selector, node).forEach((item) => {
        item.style.setProperty('--delay-offset-multiplier', offset)
        offset++
      })
    })
  }

  function intersectionWatcher(node, instant = false) {
    if (!node) return null

    const margin = window.matchMedia(getMediaQuery('above-720')).matches ? 200 : 100
    const threshold = instant ? 0 : Math.min(margin / (node.offsetHeight || 1), 0.5)

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add('is-visible')
          observer.disconnect()
        }
      },
      { threshold }
    )

    observer.observe(node)

    return {
      destroy() {
        observer.disconnect()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Slide animation (replaces the slide-anim npm dependency)
   * ---------------------------------------------------------------------- */

  const slideTimers = new WeakMap()

  function clearSlideStyles(el) {
    el.style.removeProperty('height')
    el.style.removeProperty('overflow')
    el.style.removeProperty('transition-duration')
    el.style.removeProperty('transition-property')
  }

  function slideStop(el) {
    if (!el) return
    const timer = slideTimers.get(el)
    if (timer) {
      clearTimeout(timer.id)
      slideTimers.delete(el)
      // Freeze at the current height so a new animation can pick up from here
      el.style.height = `${el.offsetHeight}px`
      el.style.removeProperty('transition-duration')
      el.style.removeProperty('transition-property')
      timer.resolve()
    }
  }

  function slideDown(el, duration = 300) {
    if (!el) return Promise.resolve()
    slideStop(el)

    // Start from wherever the element currently sits so interrupted animations
    // pick up mid-flight instead of snapping closed first
    const startHeight = window.getComputedStyle(el).display === 'none' ? 0 : el.offsetHeight

    el.style.removeProperty('display')
    let display = window.getComputedStyle(el).display
    if (display === 'none') display = 'block'
    el.style.display = display

    el.style.overflow = 'hidden'
    el.style.height = `${startHeight}px`
    const height = el.scrollHeight
    // Force reflow before animating
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight
    el.style.transitionProperty = 'height'
    el.style.transitionDuration = `${duration}ms`
    el.style.height = `${height}px`

    return new Promise((resolve) => {
      const id = window.setTimeout(() => {
        slideTimers.delete(el)
        clearSlideStyles(el)
        resolve()
      }, duration)
      slideTimers.set(el, { id, resolve })
    })
  }

  function slideUp(el, duration = 300) {
    if (!el) return Promise.resolve()
    slideStop(el)

    el.style.transitionProperty = 'height'
    el.style.transitionDuration = `${duration}ms`
    el.style.height = `${el.scrollHeight}px`
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight
    el.style.overflow = 'hidden'
    el.style.height = '0px'

    return new Promise((resolve) => {
      const id = window.setTimeout(() => {
        slideTimers.delete(el)
        el.style.display = 'none'
        clearSlideStyles(el)
        resolve()
      }, duration)
      slideTimers.set(el, { id, resolve })
    })
  }

  /* -------------------------------------------------------------------------
   * Focus trap (replaces the focus-trap npm dependency). Outside clicks are
   * always allowed, matching `{ allowOutsideClick: true }` in the original.
   * ---------------------------------------------------------------------- */

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[tabindex]:not([tabindex^="-"])',
    '[contenteditable]',
    'audio[controls]',
    'video[controls]',
    'summary',
  ].join(', ')

  function createFocusTrap(node) {
    let previouslyFocused = null
    let active = false

    function focusableItems() {
      return qsa(FOCUSABLE_SELECTOR, node).filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0)
    }

    function keydownHandler(event) {
      if (event.key !== 'Tab' && event.keyCode !== 9) return

      const items = focusableItems()
      if (!items.length) return

      const first = items[0]
      const last = items[items.length - 1]

      if (!node.contains(document.activeElement)) {
        event.preventDefault()
        first.focus()
        return
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    return {
      activate() {
        if (active || !node) return
        active = true
        previouslyFocused = document.activeElement
        document.addEventListener('keydown', keydownHandler)

        const items = focusableItems()
        if (items.length && !node.contains(document.activeElement)) items[0].focus()
      },
      deactivate() {
        if (!active) return
        active = false
        document.removeEventListener('keydown', keydownHandler)
        if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
          previouslyFocused.focus()
        }
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Body scroll lock (replaces the body-scroll-lock npm dependency)
   * ---------------------------------------------------------------------- */

  let scrollLockCount = 0

  function lockBodyScroll() {
    scrollLockCount++
    document.documentElement.classList.add('overflow-hidden')
    document.body.classList.add('overflow-hidden')
  }

  function unlockBodyScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1)
    if (scrollLockCount > 0) return
    document.documentElement.classList.remove('overflow-hidden')
    document.body.classList.remove('overflow-hidden')
  }

  /* -------------------------------------------------------------------------
   * Range slider (replaces nouislider)
   *
   * Renders the same `noUi-*` DOM structure the vendor stylesheet targets and
   * exposes a `el.noUiSlider` API compatible with the subset the price range
   * filter uses: `get()`, `set(values, fireSetEvent)`, `on(event, cb)`, `destroy()`.
   * ---------------------------------------------------------------------- */

  function createRangeSlider(el, options) {
    const rangeMin = parseInt(options.range.min, 10) || 0
    const rangeMax = parseInt(options.range.max, 10) || 0
    const span = rangeMax - rangeMin || 1

    const listeners = { slide: [], set: [], start: [], end: [], update: [] }
    const values = [clamp(Number(options.start[0])), clamp(Number(options.start[1]))]

    el.classList.add('noUi-target', 'noUi-ltr', 'noUi-horizontal', 'noUi-txt-dir-ltr')
    el.innerHTML = ''

    const base = document.createElement('div')
    base.className = 'noUi-base'

    const connects = document.createElement('div')
    connects.className = 'noUi-connects'
    const connect = document.createElement('div')
    connect.className = 'noUi-connect'
    connects.appendChild(connect)
    base.appendChild(connects)

    const handles = [0, 1].map((index) => {
      const origin = document.createElement('div')
      origin.className = 'noUi-origin'

      const handle = document.createElement('div')
      handle.className = `noUi-handle noUi-handle-${index === 0 ? 'lower' : 'upper'}`
      handle.setAttribute('data-handle', String(index))
      handle.setAttribute('tabindex', '0')
      handle.setAttribute('role', 'slider')
      handle.setAttribute('aria-orientation', 'horizontal')
      handle.setAttribute('aria-valuemin', String(rangeMin))
      handle.setAttribute('aria-valuemax', String(rangeMax))

      const attributes = (options.handleAttributes || [])[index] || {}
      Object.keys(attributes).forEach((name) => {
        if (attributes[name]) handle.setAttribute(name, attributes[name])
      })

      const touchArea = document.createElement('div')
      touchArea.className = 'noUi-touch-area'
      handle.appendChild(touchArea)
      origin.appendChild(handle)
      base.appendChild(origin)

      return { origin, handle }
    })

    el.appendChild(base)

    function clamp(value) {
      if (isNaN(value)) return rangeMin
      return Math.min(rangeMax, Math.max(rangeMin, value))
    }

    function percent(value) {
      return ((value - rangeMin) / span) * 100
    }

    function render() {
      const lower = percent(values[0])
      const upper = percent(values[1])

      handles[0].origin.style.transform = `translate(${lower - 100}%, 0)`
      handles[1].origin.style.transform = `translate(${upper - 100}%, 0)`
      handles[0].origin.style.zIndex = values[0] > (rangeMin + rangeMax) / 2 ? '5' : '4'
      handles[1].origin.style.zIndex = '5'
      connect.style.transform = `translate(${lower}%, 0) scale(${(upper - lower) / 100}, 1)`

      handles.forEach(({ handle }, index) => {
        handle.setAttribute('aria-valuenow', String(values[index]))
      })
    }

    function fire(event) {
      listeners[event].forEach((callback) => callback(values.map(String), event))
    }

    function valueFromEvent(clientX) {
      const rect = base.getBoundingClientRect()
      if (!rect.width) return rangeMin
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
      return Math.round(rangeMin + ratio * span)
    }

    function setHandleValue(index, value) {
      const next = index === 0 ? Math.min(clamp(value), values[1]) : Math.max(clamp(value), values[0])
      if (next === values[index]) return false
      values[index] = next
      render()
      return true
    }

    let dragIndex = null

    function pointerDown(event) {
      const handle = event.target.closest('.noUi-handle')
      if (!handle) return
      dragIndex = parseInt(handle.getAttribute('data-handle'), 10)
      handle.classList.add('noUi-active')
      el.classList.add('noUi-state-drag')
      if (handle.setPointerCapture && event.pointerId != null) {
        try {
          handle.setPointerCapture(event.pointerId)
        } catch (e) {
          /* capture is best-effort */
        }
      }
      fire('start')
      event.preventDefault()
    }

    function pointerMove(event) {
      if (dragIndex === null) return
      if (setHandleValue(dragIndex, valueFromEvent(event.clientX))) {
        fire('slide')
        fire('update')
      }
    }

    function pointerUp() {
      if (dragIndex === null) return
      dragIndex = null
      handles.forEach(({ handle }) => handle.classList.remove('noUi-active'))
      el.classList.remove('noUi-state-drag')
      fire('set')
      fire('end')
    }

    function keydown(event) {
      const handle = event.target.closest('.noUi-handle')
      if (!handle) return

      const index = parseInt(handle.getAttribute('data-handle'), 10)
      const step = Math.max(1, Math.round(span / 100))
      let next = null

      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = values[index] - step
      else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = values[index] + step
      else if (event.key === 'Home') next = rangeMin
      else if (event.key === 'End') next = rangeMax

      if (next === null) return
      event.preventDefault()

      if (setHandleValue(index, next)) {
        fire('slide')
        fire('update')
        fire('set')
      }
    }

    function trackClick(event) {
      if (event.target.closest('.noUi-handle')) return
      const value = valueFromEvent(event.clientX)
      const index = Math.abs(value - values[0]) <= Math.abs(value - values[1]) ? 0 : 1
      if (setHandleValue(index, value)) {
        fire('slide')
        fire('update')
        fire('set')
      }
    }

    el.addEventListener('pointerdown', pointerDown)
    document.addEventListener('pointermove', pointerMove)
    document.addEventListener('pointerup', pointerUp)
    document.addEventListener('pointercancel', pointerUp)
    el.addEventListener('keydown', keydown)
    el.addEventListener('click', trackClick)

    render()

    el.noUiSlider = {
      get() {
        return values.map(String)
      },

      set(input, fireSetEvent = true) {
        const next = toArray(input)
        let changed = false

        next.forEach((value, index) => {
          if (value === null || value === undefined || value === '') return
          const parsed = clamp(Number(value))
          if (values[index] !== parsed) {
            values[index] = parsed
            changed = true
          }
        })

        if (values[0] > values[1]) values[0] = values[1]

        render()

        if (changed) fire('update')
        if (fireSetEvent) fire('set')
      },

      on(event, callback) {
        if (listeners[event]) listeners[event].push(callback)
      },

      destroy() {
        el.removeEventListener('pointerdown', pointerDown)
        document.removeEventListener('pointermove', pointerMove)
        document.removeEventListener('pointerup', pointerUp)
        document.removeEventListener('pointercancel', pointerUp)
        el.removeEventListener('keydown', keydown)
        el.removeEventListener('click', trackClick)
        el.classList.remove('noUi-target', 'noUi-ltr', 'noUi-horizontal', 'noUi-txt-dir-ltr')
        el.innerHTML = ''
        delete el.noUiSlider
      },
    }

    return el.noUiSlider
  }

  /* -------------------------------------------------------------------------
   * Price range filter
   * ---------------------------------------------------------------------- */

  function priceRange(container) {
    const inputs = qsa('input', container)
    const minInput = inputs[0]
    const maxInput = inputs[1]
    const slider = qs('[data-range-slider]', container)

    if (!minInput || !maxInput || !slider) return null

    const events = [listen(inputs, 'change', onRangeChange)]
    const accessibility = strings.accessibility || {}

    let min = Math.floor(minInput.value ? minInput.value : minInput.getAttribute('min'))
    let max = Math.floor(maxInput.value ? maxInput.value : maxInput.getAttribute('max'))

    createRangeSlider(slider, {
      start: [
        minInput.value ? minInput.value : minInput.getAttribute('min'),
        maxInput.value ? maxInput.value : maxInput.getAttribute('max'),
      ],
      handleAttributes: [
        { 'aria-label': accessibility.range_lower },
        { 'aria-label': accessibility.range_upper },
      ],
      connect: true,
      range: {
        min: parseInt(minInput.getAttribute('min'), 10),
        max: parseInt(maxInput.getAttribute('max'), 10),
      },
    })

    slider.noUiSlider.on('slide', (values) => {
      const [minNew, maxNew] = values
      minInput.value = Math.floor(minNew)
      maxInput.value = Math.floor(maxNew)
      setMinAndMaxValues()
    })

    slider.noUiSlider.on('set', (values) => {
      const minNew = Math.floor(values[0])
      const maxNew = Math.floor(values[1])

      if (minNew != min) {
        minInput.value = minNew
        minInput.dispatchEvent(new Event('change', { bubbles: true }))
        min = Math.floor(minInput.value ? minInput.value : minInput.getAttribute('min'))
      }

      if (maxNew != max) {
        maxInput.value = maxNew
        maxInput.dispatchEvent(new Event('change', { bubbles: true }))
        max = Math.floor(maxInput.value ? maxInput.value : maxInput.getAttribute('max'))
      }

      setMinAndMaxValues()
    })

    setMinAndMaxValues()

    function setMinAndMaxValues() {
      if (maxInput.value) minInput.setAttribute('max', maxInput.value)
      if (minInput.value) maxInput.setAttribute('min', minInput.value)
      if (minInput.value === '') maxInput.setAttribute('min', 0)
      if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'))
    }

    function adjustToValidValues(input) {
      const value = Number(input.value)
      const minNew = Number(input.getAttribute('min'))
      const maxNew = Number(input.getAttribute('max'))

      if (value < minNew) input.value = minNew
      if (value > maxNew) input.value = maxNew
    }

    function onRangeChange(event) {
      adjustToValidValues(event.currentTarget)
      setMinAndMaxValues()

      if (minInput.value === '' && maxInput.value === '') return

      let [currentMin, currentMax] = slider.noUiSlider.get()
      currentMin = Math.floor(currentMin)
      currentMax = Math.floor(currentMax)

      if (currentMin !== Math.floor(minInput.value)) slider.noUiSlider.set([minInput.value, null])
      if (currentMax !== Math.floor(maxInput.value)) slider.noUiSlider.set([null, maxInput.value])
    }

    return {
      validateRange() {
        setMinAndMaxValues()
      },

      reset() {
        slider.noUiSlider.set([minInput.getAttribute('min'), maxInput.getAttribute('max')], false)
        minInput.value = ''
        maxInput.value = ''
        min = Math.floor(minInput.getAttribute('min'))
        max = Math.floor(maxInput.getAttribute('max'))
        setMinAndMaxValues()
      },

      unload() {
        events.forEach((unsubscribe) => unsubscribe())
        slider.noUiSlider && slider.noUiSlider.destroy()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Filtering — reads the filter forms and builds the search params
   * ---------------------------------------------------------------------- */

  function escapeQuotes(str) {
    const escapeMap = { '"': '\\"', "'": "\\'" }
    return String(str).replace(/"|'/g, (match) => escapeMap[match])
  }

  function filtering(container) {
    const forms = qsa('[data-filter-form]', container)
    let searchParams = ''

    setParams()

    function setParams(form) {
      const target = form || forms[0]
      if (!target) {
        searchParams = ''
        return
      }
      searchParams = new URLSearchParams(new FormData(target)).toString()
    }

    // Takes the updated form element and updates all other forms with the updated values
    function syncForms(target) {
      if (!target) return

      qsa('[data-filter-item-input]', target).forEach((targetInput) => {
        if (targetInput.type === 'checkbox' || targetInput.type === 'radio') {
          const { valueEscaped } = targetInput.dataset
          qsa(
            `input[name='${escapeQuotes(targetInput.name)}'][data-value-escaped='${escapeQuotes(valueEscaped)}']`
          ).forEach((input) => {
            input.checked = targetInput.checked
          })
        } else {
          qsa(`input[name='${escapeQuotes(targetInput.name)}']`).forEach((input) => {
            input.value = targetInput.value
          })
        }
      })
    }

    // When filters are removed, uncheck every input for that filter
    function uncheckFilters(targets) {
      if (!targets) return

      let selector

      targets.forEach((target) => {
        if (!target || !target.dataset) return
        selector = selector ? `, ${selector}` : ''
        const { name, valueEscaped } = target.dataset
        selector = `input[name='${escapeQuotes(name)}'][data-value-escaped='${escapeQuotes(valueEscaped)}']${selector}`
      })

      if (!selector) return

      qsa(selector, container).forEach((input) => {
        input.checked = false
      })
    }

    function clearRangeInputs() {
      qsa('[data-range-input]', container).forEach((input) => {
        input.value = ''
      })
    }

    return {
      getState() {
        return { url: searchParams }
      },

      filtersUpdated(target, cb) {
        syncForms(target)
        setParams(target)
        bus.emit('filters:updated')
        return cb(this.getState())
      },

      removeFilters(target, cb) {
        uncheckFilters(target)
        setParams()
        bus.emit(FILTER_REMOVED)
        return cb(this.getState())
      },

      removeRange(cb) {
        clearRangeInputs()
        setParams()
        return cb(this.getState())
      },

      clearAll(cb) {
        searchParams = ''
        forms.forEach((form) => form.reset())
        return cb(this.getState())
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Filter handler — wires the filter events up to the section's render callback
   * ---------------------------------------------------------------------- */

  function filterHandler({ container, renderCB }) {
    const filters = filtering(container)

    const subscriptions = [
      bus.on(FILTERS_REMOVE, ({ target }) => {
        filters.removeFilters(target, (data) => renderCB(data.url))
      }),

      bus.on(RANGE_REMOVE, () => {
        filters.removeRange((data) => renderCB(data.url))
      }),

      bus.on(FILTERS_UPDATE, ({ target }) => {
        filters.filtersUpdated(target, (data) => renderCB(data.url))
      }),

      bus.on(EVERYTHING_CLEAR, () => {
        filters.clearAll((data) => renderCB(data.url))
      }),
    ]

    const removeFilterHandler = (e) => {
      const target = e.target.closest('[data-remove-filter]')
      if (!target) return
      e.preventDefault()
      bus.emit(FILTERS_REMOVE, { target: [target] })
    }

    container.addEventListener('click', removeFilterHandler)
    window.addEventListener('popstate', onPopstate)

    function onPopstate() {
      // If the Storista widget exists on the page, block all popstate events
      if (qs('[data-type="storista"]')) return

      const url = new URL(window.location)
      renderCB(url.search.replace('?', ''), false)
    }

    return {
      unload() {
        container.removeEventListener('click', removeFilterHandler)
        subscriptions.forEach((unsubscribe) => unsubscribe())
        window.removeEventListener('popstate', onPopstate)
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Filter drawer animation
   * ---------------------------------------------------------------------- */

  function animateFilterDrawer(node) {
    qsa('[data-filter-modal]', node).forEach((flyout) => {
      delayOffset(flyout, ['.animation--filter-drawer-item'])
    })

    return {
      open(flyout) {
        addClass(flyout, 'animation--filter-bar-revealed')
      },
      close(flyouts) {
        removeClass(flyouts, 'animation--filter-bar-revealed')
      },
    }
  }

  function animateFilterSidebar(node) {
    delayOffset(qs('.filter-sidebar-inner', node), ['.animation--filter-drawer-item'])

    return {
      open(sidebar) {
        addClass(sidebar, 'animation--filter-sidebar-revealed')
      },
      close(sidebar) {
        removeClass(sidebar, 'animation--filter-sidebar-revealed')
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Desktop filter bar
   * ---------------------------------------------------------------------- */

  const barSel = {
    bar: '[data-filter-bar]',
    dropdownToggle: '[data-dropdown-toggle]',
    group: '[data-filter-group]',
    groupLabels: '[data-filter-group-label]',
    groupValues: '[data-filter-group-values]',
    groupReset: '[data-filter-group-reset]',
    groupHeader: '[data-group-values-header]',
    priceRange: '[data-price-range]',
    rangeInput: '[data-range-input]',
    removeRange: '[data-remove-range]',
    filterInputs: '[data-filter-item-input]',
    sortInputs: '[data-sort-item-input]',
    resultsCount: '[data-results-count]',
    activeFilters: '[data-active-filters]',
    clearAll: '[data-clear-all-filters]',
  }

  function filterBar(node) {
    if (!node) return false

    // `node` is the section container. `container` is the filter bar itself, so
    // the filter bar and filter drawer keep separate DOM scopes.
    const container = qs(barSel.bar, node)
    if (!container) return false

    const groupLabels = qsa(barSel.groupLabels, container)
    const rangeInputs = qsa(barSel.rangeInput, container)
    const rangeContainer = qs(barSel.priceRange, container)
    const filterDebounce = debounce()

    let focusTrap = null
    let range = null

    if (rangeContainer) range = priceRange(rangeContainer)

    const events = [
      listen(window, 'click', clickHandler),
      listen(container, 'change', changeHandler),
      bus.on(FILTER_REMOVED, () => syncActiveStates()),
      listen(container, 'keydown', ({ keyCode }) => {
        if (keyCode === 27) closeGroups()
      }),
    ]

    function clickHandler(e) {
      const group = e.target.closest(barSel.group)
      const dropdownToggle = e.target.closest(barSel.dropdownToggle)
      const groupReset = e.target.closest(barSel.groupReset)
      const removeRange = e.target.closest(barSel.removeRange)
      const clearAll = e.target.closest(barSel.clearAll)

      // Don't close the groups if the click happened on a filter in a group
      if (!group) closeGroups()

      if (dropdownToggle) toggleDropdown(dropdownToggle)
      if (groupReset) handleGroupReset(groupReset)

      if (removeRange) {
        e.preventDefault()
        priceRangeRemove()
      }

      if (clearAll) {
        e.preventDefault()
        clearAllFilters()
      }
    }

    function clearAllFilters() {
      range && range.reset()
      qsa(barSel.filterInputs, container).forEach((input) => {
        input.checked = false
      })
      bus.emit(FILTERS_UPDATE, { target: container })
    }

    function handleGroupReset(groupReset) {
      const group = groupReset.closest(barSel.groupValues)
      if (!group) return

      if (group.dataset.filterType === 'price_range') {
        priceRangeRemove()
      } else {
        qsa(barSel.filterInputs, group).forEach((input) => {
          input.checked = false
        })
        bus.emit(FILTERS_UPDATE, { target: container })
      }
    }

    function priceRangeRemove() {
      range && range.reset()
      checkForActiveFilters()
      bus.emit(FILTERS_UPDATE, { target: container })
    }

    function changeHandler(e) {
      const filterInput = e.target.closest(
        `${barSel.bar} ${barSel.filterInputs}, ${barSel.bar} ${barSel.sortInputs}`
      )
      const rangeInput = e.target.closest(`${barSel.bar} ${barSel.rangeInput}`)

      if (filterInput) {
        checkForActiveFilters()
        filterChange(filterInput)
      } else if (rangeInput) {
        checkForActiveFilters()
        filterChange(rangeInput)
      }
    }

    function closeGroups() {
      groupLabels.forEach(hideDropdown)
    }

    function toggleDropdown(button) {
      const ariaExpanded = button.getAttribute('aria-expanded') === 'true'
      closeGroups()
      if (!ariaExpanded) showDropdown(button)
      else hideDropdown(button)
    }

    function showDropdown(button) {
      const group = button.closest(barSel.group)
      button.setAttribute('aria-expanded', true)

      const dropdown = qs(`#${button.getAttribute('aria-controls')}`, container)
      if (!dropdown) return

      const { dropdownToggle } = button.dataset

      if (dropdownToggle === 'filter-bar-filters') {
        slideDown(dropdown).then(() => dropdown.setAttribute('aria-hidden', false))
        return
      }

      dropdown.setAttribute('aria-hidden', false)

      if (group) {
        addClass(group, 'filter-group--active')
        positionGroup(group, dropdown)

        // Lock the filter bar to stop horizontal scrolling
        addClass(container, 'filter-bar--wash-active')
        focusTrap = createFocusTrap(group)
        focusTrap.activate()
      }
    }

    function hideDropdown(button) {
      const group = button.closest(barSel.group)
      removeClass(container, 'filter-bar--wash-active')
      button.setAttribute('aria-expanded', false)

      const dropdown = qs(`#${button.getAttribute('aria-controls')}`, container)
      if (!dropdown) return

      dropdown.setAttribute('aria-hidden', true)

      if (button.dataset.dropdownToggle === 'filter-bar-filters') {
        slideUp(dropdown)
      } else if (group) {
        removeClass(group, 'filter-group--active')
        focusTrap && focusTrap.deactivate()
      }
    }

    // Flip the dropdown to the right edge of its toggle when it would otherwise
    // overflow the filter bar
    function positionGroup(group, dropdown) {
      removeClass(dropdown, 'filter-group__values--right')

      const parentBounds = group.parentElement.getBoundingClientRect()
      const groupBounds = group.getBoundingClientRect()
      const dropdownBounds = dropdown.getBoundingClientRect()

      if (groupBounds.x + dropdownBounds.width >= parentBounds.width) {
        addClass(dropdown, 'filter-group__values--right')
      }
    }

    function updateGroupPositions() {
      qsa(barSel.dropdownToggle, container).forEach((button) => {
        if (button.getAttribute('aria-expanded') !== 'true') return
        const group = button.closest(barSel.group)
        const dropdown = qs(`#${button.getAttribute('aria-controls')}`, container)
        if (group && dropdown) positionGroup(group, dropdown)
      })
    }

    function filterChange(filter) {
      if (filter.classList.contains('filter-item__content--disabled')) return

      checkForActiveFilters()
      range && range.validateRange()
      filterDebounce(() => bus.emit(FILTERS_UPDATE, { target: container }), 1000)
    }

    function rangeInputsHaveValue() {
      return rangeInputs.some((input) => input.value !== '')
    }

    function containsCheckedInputs(items) {
      return items.some((input) => input.checked)
    }

    function checkForActiveFilters() {
      const activeItems = containsCheckedInputs(qsa(barSel.filterInputs, container)) || rangeInputsHaveValue()
      toggleClass(container, 'filters-active', activeItems)
    }

    function syncActiveStates() {
      const activeItems =
        (rangeInputs && rangeInputsHaveValue()) || containsCheckedInputs(qsa(barSel.filterInputs, container))
      toggleClass(container, 'filters-active', activeItems)
    }

    function renderFilters(updatedDoc) {
      // Update the contents of the filter groups. The price group is omitted
      // because it doesn't change in Liquid and there is a JS slider.
      qsa(`${barSel.bar} ${barSel.groupValues}:not([data-filter-type="price_range"])`, updatedDoc).forEach(
        (element) => {
          updateInnerHTML(`${barSel.bar} ${barSel.groupValues}#${element.id}`, updatedDoc)
        }
      )

      updateInnerHTML(`${barSel.bar} ${barSel.resultsCount}`, updatedDoc)
      updateInnerHTML(`${barSel.bar} ${barSel.activeFilters}`, updatedDoc)
      updateInnerHTML(`${barSel.bar} ${barSel.groupHeader}`, updatedDoc)

      qsa(`${barSel.bar} ${barSel.dropdownToggle}`, updatedDoc).forEach((updated) => {
        updateInnerHTML(
          `${barSel.bar} [data-dropdown-toggle="${updated.getAttribute('data-dropdown-toggle')}"]`,
          updatedDoc
        )
      })

      qsa(`${barSel.bar} ${barSel.groupHeader}`, updatedDoc).forEach((element) => {
        updateInnerHTML(
          `${barSel.bar} [data-group-values-header="${element.getAttribute('data-group-values-header')}"]`,
          updatedDoc
        )
      })

      updateGroupPositions()
    }

    return {
      renderFilters,
      unload() {
        events.forEach((unsubscribe) => unsubscribe())
        range && range.unload()
        focusTrap && focusTrap.deactivate()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Mobile filter drawer
   * ---------------------------------------------------------------------- */

  const drawerSel = {
    drawer: '[data-filter-drawer]',
    drawerTitle: '[data-filter-drawer-title]',
    filterTarget: '[data-filter-drawer-target]',
    flyouts: '[data-filter-modal]',
    button: '[data-button]',
    wash: '[data-drawer-wash]',
    sort: '[data-sort]',
    close: '[data-close-icon]',
    group: '.filter-drawer__group',
    groupToggle: '[data-drawer-group-toggle]',
    groupContents: '.filter-drawer__group-filter-wrapper',
    panel: '.filter-drawer__panel',
    priceRange: '[data-price-range]',
    resultsCount: '[data-results-count]',
    activeFilters: '[data-active-filters]',
  }

  function filterDrawer(node) {
    if (!node) return false

    const container = qs(drawerSel.drawer, node)
    if (!container) return false

    const flyouts = qsa(drawerSel.flyouts, container)
    const wash = qs(drawerSel.wash, container)
    const rangeInputs = qsa('[data-range-input]', container)
    const rangeContainer = qs(drawerSel.priceRange, container)
    const filterDrawerDebounce = debounce()

    let focusTrap = null
    let range = null
    let drawerAnimation = null

    if (shouldAnimate(node)) drawerAnimation = animateFilterDrawer(container)
    if (rangeContainer) range = priceRange(rangeContainer)

    const events = [
      listen(qsa(drawerSel.filterTarget, node), 'click', clickFlyoutTrigger),
      listen(container, 'change', changeHandler),
      listen(wash, 'click', clickWash),
      listen(qsa(drawerSel.button, container), 'click', clickButton),
      listen(qsa(drawerSel.close, container), 'click', clickWash),
      listen(container, 'keydown', ({ keyCode }) => {
        if (keyCode === 27) clickWash()
      }),
      listen(rangeInputs, 'change', rangeChanged),
      bus.on(FILTER_REMOVED, () => syncActiveStates()),
    ]

    function changeHandler(e) {
      if (e.target.classList.contains('filter-item__checkbox')) {
        filterChange(e.target)
      } else if (e.target.classList.contains('filter-item__radio')) {
        sortChange(e)
      }
    }

    function clickFlyoutTrigger(e) {
      e.preventDefault()

      const { filterDrawerTarget } = e.currentTarget.dataset
      const modal = qs(`[data-filter-modal="${filterDrawerTarget}"]`, container)
      if (!modal) return

      focusTrap = createFocusTrap(modal)

      addClass(container, 'is-fixed')

      setTimeout(() => {
        if (shouldAnimate(node) && drawerAnimation) drawerAnimation.open(modal)
        addClass(container, 'active')
        addClass(modal, 'active')
      }, 0)

      modal.setAttribute('aria-hidden', 'false')
      focusTrap.activate()
      document.body.setAttribute('data-fluorescent-overlay-open', 'true')
      lockBodyScroll()
    }

    function clickWash(e) {
      e && e.preventDefault()

      if (!container.classList.contains('is-fixed')) return

      focusTrap && focusTrap.deactivate()

      removeClass(flyouts, 'active')
      removeClass(container, 'active')
      flyouts.forEach((flyout) => flyout.setAttribute('aria-hidden', 'true'))
      document.body.setAttribute('data-fluorescent-overlay-open', 'false')
      unlockBodyScroll()

      setTimeout(() => {
        removeClass(container, 'is-fixed')
        if (shouldAnimate(node) && drawerAnimation) drawerAnimation.close(flyouts)
      }, 500)
    }

    function filterChange(filter) {
      if (filter.classList.contains('filter-item__content--disabled')) return

      checkForActiveModalItems(filter)
      range && range.validateRange()
      filterDrawerDebounce(() => bus.emit(FILTERS_UPDATE, { target: container }), 1000)
    }

    function sortChange(e) {
      checkForActiveModalItems(e.target)
      range && range.validateRange()
      bus.emit(FILTERS_UPDATE, { target: container })
    }

    function rangeChanged(e) {
      checkForActiveModalItems(e.currentTarget)

      const wrappingContainer = e.target.closest(drawerSel.group)
      wrappingContainer && toggleClass(wrappingContainer, 'filters-active', rangeInputsHaveValue())

      bus.emit(FILTERS_UPDATE, { target: container })
    }

    function clickButton(e) {
      e.preventDefault()

      const { button } = e.currentTarget.dataset
      const scope = e.currentTarget.closest(drawerSel.flyouts)

      if (button === 'close') clickWash()
      if (!scope) return

      const { filterModal } = scope.dataset

      if (filterModal === '__sort') {
        if (button === 'clear-all') {
          qsa(`[data-filter-modal="__sort"] ${drawerSel.sort}`, container).forEach((element) => {
            const input = qs('input', element)
            if (input) input.checked = false
          })
          removeClass(e.currentTarget.closest(drawerSel.panel), 'filters-active')
        }
        return
      }

      if (button === 'clear-all') {
        qsa('input', scope).forEach((input) => {
          input.checked = false
        })

        const panel = e.currentTarget.closest(drawerSel.panel)
        removeClass([...qsa(drawerSel.group, panel), panel].filter(Boolean), 'filters-active')
        range && range.reset()

        bus.emit(FILTERS_UPDATE, { target: container })
      }

      if (button === 'group_toggle') {
        const group = qs(`#${e.currentTarget.getAttribute('aria-controls')}`)
        if (!group) return

        slideStop(group)

        if (e.currentTarget.getAttribute('aria-expanded') === 'true') {
          closeGroup(e.currentTarget, group)
        } else {
          openGroup(e.currentTarget, group)
        }
      }
    }

    function openGroup(button, group) {
      slideDown(group)
      button.setAttribute('aria-expanded', true)
      group.setAttribute('aria-hidden', false)
    }

    function closeGroup(button, group) {
      slideUp(group)
      button.setAttribute('aria-expanded', false)
      group.setAttribute('aria-hidden', true)
    }

    function containsCheckedInputs(items) {
      return items.some((input) => input.checked)
    }

    function rangeInputsHaveValue() {
      return rangeInputs.some((input) => input.value !== '')
    }

    function checkForActiveModalItems(currentTarget) {
      const panel = currentTarget.closest(drawerSel.panel)
      if (!panel) return

      const activeItems = containsCheckedInputs(qsa('input', panel)) || rangeInputsHaveValue()
      toggleClass(panel, 'filters-active', activeItems)
    }

    function syncActiveStates() {
      qsa(drawerSel.panel, container).forEach((panel) => {
        let activeItems = false

        if (containsCheckedInputs(qsa('input', panel))) activeItems = true
        if (qs('[data-range-input]', panel) && rangeInputsHaveValue()) activeItems = true

        toggleClass(panel, 'filters-active', activeItems)
      })
    }

    function renderFilters(updatedDoc) {
      // Update the contents of the filter groups. The price group is omitted
      // because it doesn't change in Liquid and there is a JS slider.
      qsa(`${drawerSel.drawer} ${drawerSel.groupContents}:not(#drawer-group-price)`, updatedDoc).forEach(
        (element) => {
          updateInnerHTML(`${drawerSel.drawer} ${drawerSel.groupContents}#${element.id}`, updatedDoc)
        }
      )

      qsa(`${drawerSel.drawer} ${drawerSel.groupToggle}`, updatedDoc).forEach((element) => {
        updateInnerHTML(
          `${drawerSel.drawer} [data-drawer-group-toggle="${element.getAttribute('data-drawer-group-toggle')}"]`,
          updatedDoc
        )
      })

      updateInnerHTML(`${drawerSel.drawer} ${drawerSel.resultsCount}`, updatedDoc)
      updateInnerHTML(`${drawerSel.drawer} ${drawerSel.activeFilters}`, updatedDoc)
      updateInnerHTML(`${drawerSel.drawer} ${drawerSel.drawerTitle}`, updatedDoc)
      updateInnerHTML('[data-mobile-filters] [data-mobile-filters-toggle]', updatedDoc)
    }

    return {
      renderFilters,
      unload() {
        events.forEach((unsubscribe) => unsubscribe())
        focusTrap && focusTrap.deactivate()
        range && range.unload()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Desktop filter sidebar
   * ---------------------------------------------------------------------- */

  const sidebarSel = {
    sidebar: '[data-filter-sidebar]',
    sidebarToggle: '.filter-bar__button--filters[data-filter-location="sidebar"]',
    sidebarToggleText: '.filter-bar__button-text',
    filterBar: '[data-filter-bar]',
    filterInputs: '[data-filter-item-input]',
    button: '[data-button]',
    group: '.filter-drawer__group',
    groupToggle: '[data-drawer-group-toggle]',
    groupContents: '.filter-drawer__group-filter-wrapper',
    priceRange: '[data-price-range]',
    resultsCount: '[data-results-count]',
    activeFilters: '[data-active-filters]',
  }

  function filterSidebar(node) {
    if (!node) return false

    const container = qs(sidebarSel.sidebar, node)
    if (!container) return false

    let sidebarAnimation = null

    if (shouldAnimate(node)) {
      sidebarAnimation = animateFilterSidebar(container)

      if (container.getAttribute('aria-expanded') === 'true') sidebarAnimation.open(container)
      else sidebarAnimation.close(container)
    }

    const filterBarEl = qs(sidebarSel.filterBar, node)
    const rangeInputs = qsa('[data-range-input]', container)
    const rangeContainer = qs(sidebarSel.priceRange, container)
    const filterDebounce = debounce()

    let range = null
    if (rangeContainer) range = priceRange(rangeContainer)

    const events = [
      listen(container, 'change', changeHandler),
      listen(qs(sidebarSel.sidebarToggle, node), 'click', clickSidebarToggle),
      listen(qsa(sidebarSel.button, container), 'click', clickButton),
      listen(rangeInputs, 'change', rangeChanged),
    ]

    function clickSidebarToggle(e) {
      e.preventDefault()

      const sidebarToggle = e.currentTarget
      const { collapsedTitle, expandedTitle } = sidebarToggle.dataset
      const buttonTextEl = qs(sidebarSel.sidebarToggleText, sidebarToggle)
      const expanded = container.getAttribute('aria-expanded') === 'true'

      container.setAttribute('aria-expanded', expanded ? 'false' : 'true')
      if (buttonTextEl) buttonTextEl.innerText = expanded ? collapsedTitle : expandedTitle

      setTimeout(() => {
        if (!shouldAnimate(node) || !sidebarAnimation) return
        if (expanded) sidebarAnimation.close(container)
        else sidebarAnimation.open(container)
      }, 0)
    }

    function changeHandler(e) {
      filterChange(e.target)
    }

    function filterChange(filter) {
      if (filter.classList.contains('filter-item__content--disabled')) return

      checkForActiveFilters()
      range && range.validateRange()
      filterDebounce(() => bus.emit(FILTERS_UPDATE, { target: container }), 1000)
    }

    function rangeChanged(e) {
      const wrappingContainer = e.target.closest(sidebarSel.group)
      wrappingContainer && toggleClass(wrappingContainer, 'filters-active', rangeInputsHaveValue())

      bus.emit(FILTERS_UPDATE, { target: container })
    }

    function clickButton(e) {
      e.preventDefault()

      const { button } = e.currentTarget.dataset

      if (button === 'clear-all') {
        qsa('input', container).forEach((input) => {
          input.checked = false
        })

        range && range.reset()
        bus.emit(FILTERS_UPDATE, { target: container })
      }

      if (button === 'group_toggle') {
        const group = qs(`#${e.currentTarget.getAttribute('aria-controls')}`)
        if (!group) return

        slideStop(group)

        if (e.currentTarget.getAttribute('aria-expanded') === 'true') {
          closeGroup(e.currentTarget, group)
        } else {
          openGroup(e.currentTarget, group)
        }
      }
    }

    function openGroup(button, group) {
      slideDown(group)
      button.setAttribute('aria-expanded', true)
      group.setAttribute('aria-hidden', false)
    }

    function closeGroup(button, group) {
      slideUp(group)
      button.setAttribute('aria-expanded', false)
      group.setAttribute('aria-hidden', true)
    }

    function checkForActiveFilters() {
      const activeItems = containsCheckedInputs(qsa(sidebarSel.filterInputs, container)) || rangeInputsHaveValue()
      toggleClass(filterBarEl, 'filters-active', activeItems)
    }

    function rangeInputsHaveValue() {
      return rangeInputs.some((input) => input.value !== '')
    }

    function containsCheckedInputs(items) {
      return items.some((input) => input.checked)
    }

    function renderFilters(updatedDoc) {
      // Update the contents of the filter groups. The price group is omitted
      // because it doesn't change in Liquid and there is a JS slider.
      qsa(
        `${sidebarSel.sidebar} ${sidebarSel.groupContents}:not([data-filter-type="price_range"])`,
        updatedDoc
      ).forEach((element) => {
        updateInnerHTML(`${sidebarSel.sidebar} ${sidebarSel.groupContents}#${element.id}`, updatedDoc)
      })

      // Update the counts and labels without changing the toggled state
      qsa(`${sidebarSel.sidebar} ${sidebarSel.groupToggle}`, updatedDoc).forEach((element) => {
        updateInnerHTML(
          `${sidebarSel.sidebar} [data-drawer-group-toggle="${element.getAttribute('data-drawer-group-toggle')}"]`,
          updatedDoc
        )
      })

      updateInnerHTML(`${sidebarSel.filterBar} ${sidebarSel.resultsCount}`, updatedDoc)
      updateInnerHTML(`${sidebarSel.filterBar} ${sidebarSel.activeFilters}`, updatedDoc)
      updateInnerHTML(`${sidebarSel.filterBar} ${sidebarSel.sidebarToggle}`, updatedDoc)
    }

    return {
      renderFilters,
      unload() {
        events.forEach((unsubscribe) => unsubscribe())
        range && range.unload()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Sticky filter sidebar scroll
   * ---------------------------------------------------------------------- */

  const stickySel = {
    stickyContainer: '[data-sticky-container]',
    stickyFilterBar: '.filter-bar--sticky',
    // Row that contains the sticky column + its taller sibling (gallery / results)
    stickyRow: '.product__primary, .collection__main-area, .search__main-area',
    productGalleryMedia:
      '.product__primary-left .product__media-container.above-mobile, .product__primary-left .js-product-media-desktop, .product__primary-left .product__media',
  }

  function stickyScroll(node) {
    const stickyContainer = qs(stickySel.stickyContainer, node)
    if (!stickyContainer) return false

    const root = document.documentElement
    const stickyFilterBar = qs(stickySel.stickyFilterBar)
    const stickyParent = stickyContainer.parentElement
    const stickyRow = qs(stickySel.stickyRow, node) || (stickyParent && stickyParent.parentElement)
    const galleryMedia = qs(stickySel.productGalleryMedia, node)

    node.style.setProperty('--sticky-container-top', 0)
    root.style.setProperty('--sticky-filter-bar-height', '0px')

    let previousScrollY = window.scrollY
    let headerHeight = 0
    let stickyFilterBarHeight = 0
    let stickyContainerTop = headerHeight
    let stickyContainerTopPrevious = stickyContainerTop
    let stickyContainerHeight = stickyContainer.offsetHeight
    let stickyContainerMaxTop = stickyContainerHeight + headerHeight - window.innerHeight
    let running = true

    const scroller = () => {
      scrollHandler(window.scrollY)

      if (stickyFilterBar) stickyFilterBarHeight = stickyFilterBar.offsetHeight
      root.style.setProperty('--sticky-filter-bar-height', `${stickyFilterBarHeight}px`)
    }

    window.addEventListener('scroll', scroller)
    window.addEventListener('resize', scroller)

    const resizeObserver = new ResizeObserver(observeHeight)
    resizeObserver.observe(stickyContainer)
    if (stickyParent) resizeObserver.observe(stickyParent)
    if (stickyRow) resizeObserver.observe(stickyRow)
    if (galleryMedia) resizeObserver.observe(galleryMedia)
    resizeObserver.observe(root)

    requestAnimationFrame(updateStickyContainerTopLoop)

    // Prefer the real gallery media height. Stretched grid columns report the
    // taller sibling's height and incorrectly look like there's no travel room.
    function getAvailableHeight() {
      if (galleryMedia && galleryMedia.offsetHeight) return galleryMedia.offsetHeight

      const parentHeight = (stickyParent && stickyParent.offsetHeight) || 0
      const rowHeight = (stickyRow && stickyRow.offsetHeight) || 0
      const available = Math.max(parentHeight, rowHeight)

      return available > 0 ? available : node.offsetHeight
    }

    function observeHeight() {
      stickyContainerHeight = stickyContainer.offsetHeight
      if (stickyFilterBar) stickyFilterBarHeight = stickyFilterBar.offsetHeight

      headerHeight =
        parseInt(getComputedStyle(root).getPropertyValue('--header-desktop-sticky-height').replace(/px/gi, ''), 10) ||
        0

      const stickyContainerHeightWithHeaderAndBar = stickyContainerHeight + headerHeight + stickyFilterBarHeight
      stickyContainerMaxTop = stickyContainerHeightWithHeaderAndBar - window.innerHeight

      const availableHeight = getAvailableHeight()

      if (stickyContainerHeightWithHeaderAndBar > window.innerHeight && availableHeight > stickyContainerHeight) {
        // Keep the sticky offset aligned with the header before the first scroll
        if (stickyContainerTop === 0 && stickyContainerTopPrevious === 0 && headerHeight > 0) {
          stickyContainerTop = headerHeight
          stickyContainerTopPrevious = headerHeight
          node.style.setProperty('--sticky-container-top', `${headerHeight}px`)
        }
        addClass(node, 'has-sticky-scroll')
        scrollHandler(window.scrollY)
      } else {
        removeClass(node, 'has-sticky-scroll')
      }
    }

    function scrollHandler(y) {
      const currentScrollAmount = previousScrollY - y
      const currentScrollOffset = stickyContainerTop + currentScrollAmount

      const topMax = headerHeight + stickyFilterBarHeight
      const bottomMax = -stickyContainerMaxTop + headerHeight + stickyFilterBarHeight - 40

      stickyContainerTop = Math.max(bottomMax, Math.min(currentScrollOffset, topMax))
      previousScrollY = y
    }

    // Endless RAF loop that LERPs `--sticky-container-top` so fast scrolling
    // doesn't produce large jumps
    function updateStickyContainerTopLoop() {
      if (!running) return

      if (stickyContainerTop !== stickyContainerTopPrevious) {
        stickyContainerTopPrevious = lerp(stickyContainerTopPrevious, stickyContainerTop, 0.5)
        node.style.setProperty('--sticky-container-top', `${stickyContainerTopPrevious}px`)
      }

      requestAnimationFrame(updateStickyContainerTopLoop)
    }

    return {
      destroy() {
        running = false
        window.removeEventListener('scroll', scroller)
        window.removeEventListener('resize', scroller)
        resizeObserver.disconnect()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Collection / search reveal animations
   * ---------------------------------------------------------------------- */

  const animationSel = {
    stickyHeader: ".header[data-enable-sticky-header='true']",
    partial: '[data-partial]',
    filterBar: '[data-filter-bar]',
    mobileFilterBar: '[data-mobile-filters]',
    productItems: '.animation--item:not(.animation--item-revealed)',
  }

  function animateResults(node, { hideClass, scrollTarget }) {
    const stickyHeader = qs(animationSel.stickyHeader)
    const partial = qs(animationSel.partial, node)
    const filterbarEl = qs(animationSel.filterBar, node)
    const mobileFilterBarEl = qs(animationSel.mobileFilterBar, node)

    const filterbarObserver = filterbarEl ? intersectionWatcher(filterbarEl, true) : null
    const mobileFilterBarObserver = mobileFilterBarEl ? intersectionWatcher(mobileFilterBarEl, true) : null

    setupProductItems()

    function setupProductItems() {
      const productItems = qsa(animationSel.productItems, node)
      delayOffset(node, [animationSel.productItems])
      setTimeout(() => addClass(productItems, 'animation--item-revealed'), 0)
    }

    // Scroll to the top of the results grid after applying filters so the newly
    // filtered list is in view
    function scrollIntoView() {
      if (scrollTarget === 'partial') {
        if (!partial) return
        const offset = filterbarEl ? filterbarEl.getBoundingClientRect().height : 0
        window.scrollTo({ top: partial.getBoundingClientRect().top + window.pageYOffset - offset, behavior: 'smooth' })
        return
      }

      const stickyHeaderHeight = stickyHeader ? stickyHeader.getBoundingClientRect().height / 2 : 0
      window.scrollTo({
        top: node.getBoundingClientRect().top + window.pageYOffset - stickyHeaderHeight,
        behavior: 'smooth',
      })
    }

    return {
      updateContents() {
        setupProductItems()
        removeClass(partial, hideClass)
        scrollIntoView()
      },

      infiniteScrollReveal() {
        setupProductItems()
      },

      destroy() {
        filterbarObserver && filterbarObserver.destroy()
        mobileFilterBarObserver && mobileFilterBarObserver.destroy()
      },
    }
  }

  function animateCollection(node) {
    return animateResults(node, { hideClass: 'animation--collection-products-hide', scrollTarget: 'section' })
  }

  function animateSearch(node) {
    return animateResults(node, { hideClass: 'animation--search-products-hide', scrollTarget: 'partial' })
  }

  function animateSearchBanner(node) {
    delayOffset(node, ['.animation--section-blocks > *'])
    const observer = intersectionWatcher(node)

    return {
      destroy() {
        observer && observer.destroy()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Infinite scroll (replaces the ajaxinate npm dependency)
   *
   * Fetches the next page, appends the product items from the matching
   * container, and swaps in the next page's pagination trigger.
   * ---------------------------------------------------------------------- */

  function initInfiniteScroll(settings) {
    const options = Object.assign({ method: 'scroll', offset: 0, loadingText: 'Loading', callback: null }, settings)

    const containerElement = qs(options.container)
    const paginationElement = qs(options.pagination)

    let nextPageLinkElement = null
    let nextPageUrl = null
    let clickActive = true
    let destroyed = false

    if (!containerElement) {
      return { destroy() {} }
    }

    function initialize() {
      if (destroyed) return
      if (options.method === 'click') addClickListener()
      else addScrollListeners()
    }

    function addScrollListeners() {
      if (!paginationElement) return
      document.addEventListener('scroll', checkIfPaginationInView)
      window.addEventListener('resize', checkIfPaginationInView)
      window.addEventListener('orientationchange', checkIfPaginationInView)
    }

    function removeScrollListeners() {
      document.removeEventListener('scroll', checkIfPaginationInView)
      window.removeEventListener('resize', checkIfPaginationInView)
      window.removeEventListener('orientationchange', checkIfPaginationInView)
    }

    function addClickListener() {
      if (!paginationElement) return
      nextPageLinkElement = paginationElement.querySelector('a')
      clickActive = true
      if (nextPageLinkElement) nextPageLinkElement.addEventListener('click', preventMultipleClicks)
    }

    function removeClickListener() {
      if (nextPageLinkElement) nextPageLinkElement.removeEventListener('click', preventMultipleClicks)
    }

    function preventMultipleClicks(event) {
      event.preventDefault()
      if (!clickActive) return

      nextPageLinkElement.innerText = options.loadingText
      nextPageUrl = nextPageLinkElement.href
      clickActive = false
      loadMore()
    }

    function checkIfPaginationInView() {
      const bounds = paginationElement.getBoundingClientRect()
      const top = bounds.top - options.offset
      const bottom = bounds.bottom + options.offset

      if (top > window.innerHeight || bottom < 0) return

      nextPageLinkElement = paginationElement.querySelector('a')
      removeScrollListeners()

      if (!nextPageLinkElement) return

      nextPageLinkElement.innerText = options.loadingText
      nextPageUrl = nextPageLinkElement.href
      loadMore()
    }

    function loadMore() {
      if (!nextPageUrl) return

      fetch(nextPageUrl)
        .then((response) => response.text())
        .then((html) => {
          if (destroyed) return

          const doc = new DOMParser().parseFromString(html, 'text/html')
          const newContainer = qs(options.container, doc)
          const newPagination = qs(options.pagination, doc)

          if (newContainer) containerElement.insertAdjacentHTML('beforeend', newContainer.innerHTML)

          if (!newPagination) {
            removePaginationElement()
            if (typeof options.callback === 'function') options.callback(doc)
            return
          }

          paginationElement.innerHTML = newPagination.innerHTML

          if (typeof options.callback === 'function') options.callback(doc)

          initialize()
        })
        .catch((error) => console.error('[plp] infinite scroll failed', error))
    }

    function removePaginationElement() {
      if (paginationElement) paginationElement.innerHTML = ''
      destroy()
    }

    function destroy() {
      destroyed = true
      removeScrollListeners()
      removeClickListener()
    }

    initialize()

    return { destroy }
  }

  /* -------------------------------------------------------------------------
   * AJAX pagination (plp-ajax): pre-fetches every page of the collection, then
   * renders one page at a time client-side with a JS-built pagination control.
   * ---------------------------------------------------------------------- */

  const plpSel = {
    plpAjax: '[data-plp-ajax]',
    paginate: '[data-plp-paginate]',
    plpItem: '[data-plp-item]',
  }

  function paginationRange(currentPage, totalPages, maxVisiblePages = 5) {
    const range = []
    const half = Math.floor(maxVisiblePages / 2)

    let startPage = currentPage - half > 0 ? currentPage - half : 1
    let endPage = currentPage + half < totalPages ? currentPage + half : totalPages

    if (currentPage <= half) {
      startPage = 1
      endPage = maxVisiblePages < totalPages ? maxVisiblePages : totalPages
    }

    if (currentPage > totalPages - half) {
      startPage = totalPages - maxVisiblePages + 1 > 1 ? totalPages - maxVisiblePages + 1 : 1
      endPage = totalPages
    }

    if (startPage > 1) {
      range.push(1)
      if (startPage > 2) range.push('...')
    }

    for (let i = startPage; i <= endPage; i++) {
      range.push(i)
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) range.push('...')
      range.push(totalPages)
    }

    return range
  }

  function buildPaginationHtml(range, currentPage, queryString = '') {
    const lastPage = range[range.length - 1]
    const hrefPre = currentPage >= 1 ? `${window.location.pathname}?page=${currentPage - 1}${queryString}` : ''
    const disabledPrev = currentPage === 1 ? 'disabled' : ''
    const hrefNext = currentPage < lastPage ? `${window.location.pathname}?page=${currentPage + 1}${queryString}` : ''
    const disabledNext = currentPage == lastPage ? 'disabled' : ''

    const arrowSvg = `
    <span class="icon icon-new icon-arrow-pagination ">
      <svg viewBox="0 0 20 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path stroke="currentColor" d="m15 1 3 3.598L15 8M0 4.5h18"></path></svg>
    </span>
  `

    let markup = '<ul class="pagination__list">'

    if (currentPage >= 1) {
      markup += `
    <li class="pagination__item pagination__item--arrow">
      <a href="${hrefPre}" class="pagination__navigation-button pagination__navigation-button--previous" aria-hidden="true" ${disabledPrev} aria-label="Previous page of products">
        ${arrowSvg}
      </a>
    </li>
  `
    }

    range.forEach((page) => {
      if (page === currentPage) {
        markup += `<li class="pagination__item pagination__item--active"><span class="pagination__navigation-button">${page}</span></li>`
      } else if (typeof page === 'string') {
        markup += `<li class="pagination__item pagination__item--ellip"><span class="pagination__navigation-button">${page}</span></li>`
      } else {
        markup += `<li class="pagination__item"><a href="${window.location.pathname}?page=${page}${queryString}" class="pagination__navigation-button">${page}</a></li>`
      }
    })

    if (currentPage <= lastPage) {
      markup += `
    <li class="pagination__item pagination__item--arrow">
      <a href="${hrefNext}" class="pagination__navigation-button pagination__navigation-button--next" aria-hidden="true" ${disabledNext} aria-label="Next page of products">
        ${arrowSvg}
      </a>
    </li>
  `
    }

    markup += '</ul>'

    return `<div class="pagination fs-body-300">${markup}</div>`
  }

  function buildPagination(currentPage, totalPages, maxVisiblePages, queryString) {
    const range = paginationRange(currentPage, totalPages, maxVisiblePages)
    if (range.length > 1) return buildPaginationHtml(range, currentPage, queryString)
    return false
  }

  function fetchPage(url) {
    return fetch(url).then((response) => {
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`)
      return response.text()
    })
  }

  function fetchData(currentPage = 1, totalPages, sectionId, itemEl, queryString = '') {
    const chain = []

    for (let i = currentPage; i <= totalPages; i++) {
      chain.push(fetchPage(`${window.location.pathname}?section_id=${sectionId}&page=${i}${queryString}`))
    }

    return Promise.all(chain).then((allResults) => {
      const combinedResults = []

      allResults.forEach((htmlString) => {
        const doc = new DOMParser().parseFromString(htmlString, 'text/html')
        qsa(itemEl, doc).forEach((element) => combinedResults.push(element.innerHTML))
      })

      return combinedResults
    })
  }

  function getCurrentPage() {
    const page = new URLSearchParams(window.location.search).get('page')
    return page ? parseInt(page, 10) : 1
  }

  function paginatedResults(results, page, perPage) {
    if (page === 1) return results.slice(0, perPage)

    const from = perPage + (page - 2) * perPage
    return results.slice(from, from + perPage)
  }

  function plpAjax(container, onRendered) {
    const plpAjaxElements = qsa(plpSel.plpAjax, container)
    if (!plpAjaxElements.length) return { unload() {} }

    const { collectionItemCount, sectionId, collectionItemsPerPage } = container.dataset
    const currentPage = getCurrentPage()
    const paginateEl = qs(plpSel.paginate, container)
    const totalCount = parseInt(collectionItemCount || 0, 10)
    const pageSize = parseInt(collectionItemsPerPage || 12, 10)
    const totalPages = Math.ceil((totalCount + 1) / pageSize)
    const urlParams = new URLSearchParams(window.location.search)
    const sortBy = urlParams.get('sort_by') ? `&sort_by=${urlParams.get('sort_by')}` : ''

    let cancelled = false

    fetchData(1, totalPages, sectionId, plpSel.plpItem, sortBy)
      .then((results) => {
        if (cancelled || !results.length) return

        plpAjaxElements.forEach((plpAjaxEl) => {
          const resultsPage = paginatedResults(results, currentPage, pageSize)

          plpAjaxEl.innerHTML = ''
          resultsPage.forEach((snippet) => {
            plpAjaxEl.innerHTML += snippet
          })

          reinitProductItems(container)
          if (typeof onRendered === 'function') onRendered()
          emitCollectionUpdated()
        })

        const paginationMarkup = buildPagination(currentPage, totalPages, 4, sortBy)
        if (paginationMarkup && paginateEl) paginateEl.innerHTML = paginationMarkup
      })
      .catch((error) => console.error('[plp] ajax pagination failed', error))

    return {
      unload() {
        cancelled = true
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Collection section
   * ---------------------------------------------------------------------- */

  const collectionSel = {
    infiniteScrollContainer: '.collection__infinite-container',
    infiniteScrollTrigger: '.collection__infinite-trigger',
    partial: '[data-partial]',
    filterDrawer: '[data-filter-drawer]',
    filterBar: '[data-filter-bar]',
    loader: '.collection__loading',
    paginationItemCount: '[data-pagination-item-count]',
    productItems: '.product-item',
  }

  function initCollection(container) {
    const { collectionItemCount, paginationType } = container.dataset

    if (!parseInt(collectionItemCount, 10)) return { unload() {} }

    const paginated = paginationType === 'paginated'
    const infiniteScrollTrigger = qs(collectionSel.infiniteScrollTrigger, container)
    const filterDrawerEl = qs(collectionSel.filterDrawer, container)
    const filterbarEl = qs(collectionSel.filterBar, container)

    let partial = qs(collectionSel.partial, container)
    let paginationItemCount = qs(collectionSel.paginationItemCount, container)

    let infiniteScroll = null
    let drawer = null
    let bar = null
    let sidebar = null
    let handler = null
    let sticky = null
    let breakPointHandler = null
    let animation = null
    let ajax = null
    let mobileQuery = null

    if (filterDrawerEl || filterbarEl) {
      drawer = filterDrawer(container)
      bar = filterBar(container)
      sidebar = filterSidebar(container)
      handler = filterHandler({ container, renderCB: renderView })

      if (sidebar && container.dataset.enableStickyFilterSidebar === 'true') {
        mobileQuery = window.matchMedia(getMediaQuery('below-960'))
        initStickyScroll()
        breakPointHandler = atBreakpointChange(960, initStickyScroll)
      }
    }

    if (!paginated) initInfinite()

    reinitProductItems(container)

    if (shouldAnimate(container)) animation = animateCollection(container)

    ajax = plpAjax(container, () => {
      if (shouldAnimate(container) && animation) animation.infiniteScrollReveal()
    })

    updateGridItems()
    const resizeHandler = () => updateGridItems()
    window.addEventListener('resize', resizeHandler)

    function initStickyScroll() {
      if (mobileQuery.matches) {
        if (sticky) {
          sticky.destroy()
          sticky = null
        }
      } else if (!sticky) {
        sticky = stickyScroll(container)
      }
    }

    function initInfinite() {
      const infiniteScrollOptions = {
        container: collectionSel.infiniteScrollContainer,
        pagination: collectionSel.infiniteScrollTrigger,
        loadingText: 'Loading...',
        callback: () => {
          reinitProductItems(container)
          animation && animation.infiniteScrollReveal()
          updatePaginationCount()
          updateGridItems()
          emitCollectionUpdated()
        },
      }

      if (paginationType === 'click') infiniteScrollOptions.method = 'click'

      infiniteScroll = initInfiniteScroll(infiniteScrollOptions)
    }

    function updatePaginationCount() {
      if (!paginationItemCount || !partial) return

      const paginationStrings = strings.pagination || {}
      if (!paginationStrings.viewing) return

      const productItemCount = qsa(collectionSel.productItems, container).length
      const viewing = paginationStrings.viewing
        .replace('{{ of }}', `1-${productItemCount}`)
        .replace('{{ total }}', partial.dataset.collectionProductsCount)

      paginationItemCount.innerHTML = `${viewing} ${paginationStrings.products || ''}`
    }

    function renderView(searchParams, updateHistory = true) {
      const url = `${window.location.pathname}?section_id=${container.dataset.sectionId}&${searchParams}`
      const loading = qs(collectionSel.loader, container)

      addClass(partial, 'animation--collection-products-hide')
      addClass(loading, 'is-active')

      fetch(url)
        .then((res) => res.text())
        .then((res) => {
          if (updateHistory) updateURLHash(searchParams)

          const doc = new DOMParser().parseFromString(res, 'text/html')
          const updatedPartial = qs(collectionSel.partial, doc)

          if (updatedPartial) {
            partial.innerHTML = updatedPartial.innerHTML
            partial.dataset.collectionProductsCount = updatedPartial.dataset.collectionProductsCount
          }

          animation && animation.updateContents()

          if (!paginated && infiniteScrollTrigger) {
            infiniteScrollTrigger.innerHTML = ''
            infiniteScroll && infiniteScroll.destroy()
            initInfinite()
          }

          drawer && drawer.renderFilters(doc)
          bar && bar.renderFilters(doc)
          sidebar && sidebar.renderFilters(doc)

          reinitProductItems(container)
          paginationItemCount = qs(collectionSel.paginationItemCount, container)

          ajax && ajax.unload()
          ajax = plpAjax(container, () => {
            if (shouldAnimate(container) && animation) animation.infiniteScrollReveal()
          })

          updateGridItems()
          removeClass(loading, 'is-active')
          emitCollectionUpdated()
        })
        .catch((error) => {
          removeClass(loading, 'is-active')
          removeClass(partial, 'animation--collection-products-hide')
          console.error('[plp] failed to render collection', error)
        })
    }

    function updateURLHash(searchParams) {
      history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      )
    }

    // Tag the first item of each visual row so the grid can style row edges
    function updateGridItems() {
      const productItems = qsa(collectionSel.productItems, container)
      const columnsDesktop = parseInt(container.dataset.collectionColumnsDesktop, 10)
      const columnsMobile = parseInt(container.dataset.collectionColumnsMobile, 10)
      const columns = window.innerWidth >= 768 ? columnsDesktop || 4 : columnsMobile || 2

      productItems.forEach((item, index) => {
        item.classList.remove('first-item')
        if (index % columns === 0) item.classList.add('first-item')
      })
    }

    return {
      unload() {
        window.removeEventListener('resize', resizeHandler)
        infiniteScroll && infiniteScroll.destroy()
        handler && handler.unload()
        drawer && drawer.unload()
        bar && bar.unload()
        sidebar && sidebar.unload()
        sticky && sticky.destroy()
        breakPointHandler && breakPointHandler.unload()
        ajax && ajax.unload()
        animation && animation.destroy()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Search section
   * ---------------------------------------------------------------------- */

  const searchSel = {
    searchSection: '.search',
    searchBanner: '.search-header',
    infiniteScrollContainer: '.search__infinite-container',
    infiniteScrollTrigger: '.search__infinite-trigger',
    partial: '[data-partial]',
    filterBar: '[data-filter-bar]',
    loader: '.search__loading',
    paginationItemCount: '[data-pagination-item-count]',
    searchItems: '.product-item, .search-item',
  }

  function initSearch(container) {
    const searchBannerEl = qs(searchSel.searchBanner, container)

    let bannerAnimation = null
    if (shouldAnimate(searchBannerEl)) bannerAnimation = animateSearchBanner(searchBannerEl)

    const { searchItemCount, paginationType } = container.dataset

    if (!parseInt(searchItemCount, 10)) {
      return {
        unload() {
          bannerAnimation && bannerAnimation.destroy()
        },
      }
    }

    const searchSectionEl = qs(searchSel.searchSection, container)
    const filterBarEl = qs(searchSel.filterBar, container)
    const paginated = paginationType === 'paginated'
    const infiniteScrollTrigger = qs(searchSel.infiniteScrollTrigger, container)

    let partial = qs(searchSel.partial, container)
    let paginationItemCount = qs(searchSel.paginationItemCount, container)

    let infiniteScroll = null
    let drawer = null
    let bar = null
    let sidebar = null
    let handler = null
    let sticky = null
    let breakPointHandler = null
    let animation = null
    let mobileQuery = null

    if (filterBarEl) {
      drawer = filterDrawer(searchSectionEl)
      bar = filterBar(searchSectionEl)
      sidebar = filterSidebar(searchSectionEl)
      handler = filterHandler({ container: searchSectionEl, renderCB: renderView })

      if (sidebar && searchSectionEl.dataset.enableStickyFilterSidebar === 'true') {
        mobileQuery = window.matchMedia(getMediaQuery('below-960'))
        initStickyScroll()
        breakPointHandler = atBreakpointChange(960, initStickyScroll)
      }
    }

    if (!paginated) initInfinite()

    reinitProductItems(container)

    if (shouldAnimate(searchSectionEl)) animation = animateSearch(searchSectionEl)

    function initStickyScroll() {
      if (mobileQuery.matches) {
        if (sticky) {
          sticky.destroy()
          sticky = null
        }
      } else if (!sticky) {
        sticky = stickyScroll(searchSectionEl)
      }
    }

    function initInfinite() {
      const infiniteScrollOptions = {
        container: searchSel.infiniteScrollContainer,
        pagination: searchSel.infiniteScrollTrigger,
        loadingText: 'Loading...',
        callback: () => {
          reinitProductItems(container)
          animation && animation.infiniteScrollReveal()
          updatePaginationCount()
          emitCollectionUpdated()
        },
      }

      if (paginationType === 'click') infiniteScrollOptions.method = 'click'

      infiniteScroll = initInfiniteScroll(infiniteScrollOptions)
    }

    function updatePaginationCount() {
      if (!paginationItemCount || !partial) return

      const paginationStrings = strings.pagination || {}
      if (!paginationStrings.viewing) return

      const searchItemsCount = qsa(searchSel.searchItems, container).length
      const viewing = paginationStrings.viewing
        .replace('{{ of }}', `1-${searchItemsCount}`)
        .replace('{{ total }}', partial.dataset.searchResultsCount)

      paginationItemCount.innerHTML = `${viewing} ${paginationStrings.results || ''}`
    }

    function renderView(searchParams, updateHistory = true) {
      const url = `${window.location.pathname}?section_id=${container.dataset.sectionId}&${searchParams}`
      const loading = qs(searchSel.loader, container)

      addClass(loading, 'is-active')

      fetch(url)
        .then((res) => res.text())
        .then((res) => {
          if (updateHistory) updateURLHash(searchParams)

          const doc = new DOMParser().parseFromString(res, 'text/html')
          const updatedPartial = qs(searchSel.partial, doc)

          if (updatedPartial) {
            partial.innerHTML = updatedPartial.innerHTML
            partial.dataset.searchResultsCount = updatedPartial.dataset.searchResultsCount
          }

          animation && animation.updateContents()

          if (!paginated && infiniteScrollTrigger) {
            infiniteScrollTrigger.innerHTML = ''
            infiniteScroll && infiniteScroll.destroy()
            initInfinite()
          }

          drawer && drawer.renderFilters(doc)
          bar && bar.renderFilters(doc)
          sidebar && sidebar.renderFilters(doc)

          reinitProductItems(container)
          paginationItemCount = qs(searchSel.paginationItemCount, container)

          removeClass(loading, 'is-active')
          emitCollectionUpdated()
        })
        .catch((error) => {
          removeClass(loading, 'is-active')
          console.error('[plp] failed to render search results', error)
        })
    }

    function updateURLHash(searchParams) {
      history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      )
    }

    return {
      unload() {
        infiniteScroll && infiniteScroll.destroy()
        handler && handler.unload()
        drawer && drawer.unload()
        bar && bar.unload()
        sidebar && sidebar.unload()
        sticky && sticky.destroy()
        breakPointHandler && breakPointHandler.unload()
        animation && animation.destroy()
        bannerAnimation && bannerAnimation.destroy()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Auto init / theme editor section lifecycle
   * ---------------------------------------------------------------------- */

  const SECTION_SELECTOR = '[data-section-type="collection"], [data-section-type="search"]'
  const instances = new WeakMap()

  function initContainer(container) {
    if (instances.has(container)) return

    const { sectionType } = container.dataset
    let instance = null

    if (sectionType === 'collection') instance = initCollection(container)
    else if (sectionType === 'search') instance = initSearch(container)

    if (instance) instances.set(container, instance)
  }

  function unloadContainer(container) {
    const instance = instances.get(container)
    instance && instance.unload && instance.unload()
    instances.delete(container)
  }

  function autoInit(root = document) {
    qsa(SECTION_SELECTOR, root).forEach(initContainer)

    // shopify:section:load hands us the section wrapper itself
    if (root instanceof Element && root.matches && root.matches(SECTION_SELECTOR)) initContainer(root)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => autoInit())
  } else {
    autoInit()
  }

  function sectionContainers(event) {
    const sectionId = event.detail && event.detail.sectionId
    const containers = sectionId ? qsa(`[data-section-id="${sectionId}"]`).filter((el) => el.matches(SECTION_SELECTOR)) : []

    if (containers.length) return containers
    if (!(event.target instanceof Element)) return []

    const scoped = qsa(SECTION_SELECTOR, event.target)
    if (event.target.matches(SECTION_SELECTOR)) scoped.push(event.target)

    return scoped
  }

  document.addEventListener('shopify:section:load', (event) => {
    sectionContainers(event).forEach(initContainer)
  })

  document.addEventListener('shopify:section:unload', (event) => {
    sectionContainers(event).forEach(unloadContainer)
  })
})()
